import React, { useState } from 'react';
import {
    Button,
    Card,
    Select,
    Space,
    Tag,
    Typography,
    Row,
    Col,
    Statistic,
    Popconfirm,
    message,
} from 'antd';
import {
    TeamOutlined,
    SendOutlined,
    GlobalOutlined,
} from '@ant-design/icons';
import {
    useGetWaitlistEntriesQuery,
    useGetWaitlistStatsQuery,
    useInviteWaitlistEntryMutation,
    useUpdateWaitlistEntryMutation,
    useGetTemplateTypesQuery,
    useGetQualificationConfigQuery,
} from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import { businessModelLabel, productCategoryLabel } from '../../utils/qualificationLabels';
import { waitlistReason } from '../../utils/waitlistReason';
import { ResponsiveTable } from '../components/ResponsiveTable';
import { ColumnsType } from 'antd/es/table';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';

const { Title } = Typography;

const STATUS_COLORS: Record<string, string> = {
    PENDING: 'blue',
    NOTIFIED: 'orange',
    CONVERTED: 'green',
};

const formatTemplateTypeName = (type: string): string => {
    return type
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const AdminWaitlistPage: React.FC = () => {
    const intl = useIntl();
    const { user } = useAuth();
    const jwtToken = user?.jwtToken || '';
    const [messageApi, contextHolder] = message.useMessage();

    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

    const { data: entries, isLoading } = useGetWaitlistEntriesQuery({
        jwtToken,
        status: statusFilter,
    });

    const { data: stats } = useGetWaitlistStatsQuery({ jwtToken });
    const [inviteEntry, inviteResult] = useInviteWaitlistEntryMutation();
    const [updateEntry] = useUpdateWaitlistEntryMutation();
    const { data: templateTypesData } = useGetTemplateTypesQuery({ jwtToken });
    // Needed to turn the stored answer codes into labels and to replay the
    // qualification decision that put each entry here.
    const { data: qualificationConfig } = useGetQualificationConfigQuery();

    const templateTypeOptions = (templateTypesData?.data || []).map((t) => ({
        value: t,
        label: formatTemplateTypeName(t),
    }));

    const handleInvite = async (id: string, email: string) => {
        const result = await inviteEntry({ jwtToken, id });
        if ('data' in result) {
            messageApi.success(intl.formatMessage({ id: 'admin.waitlist.inviteSent' }, { email }));
        } else {
            messageApi.error(intl.formatMessage({ id: 'admin.waitlist.inviteError' }));
        }
    };

    const handleTemplateTypeChange = async (id: string, templateType: string | null) => {
        const result = await updateEntry({ jwtToken, id, templateType });
        if (!('data' in result)) {
            messageApi.error(intl.formatMessage({ id: 'admin.waitlist.updateError' }));
        }
    };

    const columns: ColumnsType<any> = [
        {
            title: intl.formatMessage({ id: 'admin.waitlist.column.name' }),
            key: 'name',
            render: (_, record) => `${record.firstName} ${record.lastName}`,
        },
        {
            title: intl.formatMessage({ id: 'admin.waitlist.column.email' }),
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: intl.formatMessage({ id: 'admin.waitlist.column.businessName' }),
            dataIndex: 'businessName',
            key: 'businessName',
        },
        {
            title: intl.formatMessage({ id: 'admin.waitlist.column.websiteUrl' }),
            dataIndex: 'websiteUrl',
            key: 'websiteUrl',
            render: (value: string | null) =>
                value ? (
                    <a href={value} target="_blank" rel="noopener noreferrer">
                        <GlobalOutlined style={{ marginRight: 4 }} />
                        {value}
                    </a>
                ) : (
                    <span style={{ color: '#bfbfbf' }}>—</span>
                ),
        },
        {
            title: intl.formatMessage({ id: 'admin.waitlist.column.productCategory' }),
            dataIndex: 'productCategory',
            key: 'productCategory',
            render: (value: string | null) =>
                productCategoryLabel(qualificationConfig, value) ?? (
                    <span style={{ color: '#bfbfbf' }}>—</span>
                ),
        },
        {
            title: intl.formatMessage({ id: 'admin.waitlist.column.businessModel' }),
            dataIndex: 'businessModel',
            key: 'businessModel',
            render: (value: string | null) =>
                businessModelLabel(qualificationConfig, value) ?? (
                    <span style={{ color: '#bfbfbf' }}>—</span>
                ),
        },
        {
            // Replayed from the two answers against QUALIFICATION_CONFIG — the
            // waitlist row itself stores no reason.
            // Where the entry came from. Recorded on the row since SYR-106 —
            // before that the table had no way to tell a Charter rejection from
            // an open-signup one.
            title: intl.formatMessage({ id: 'admin.waitlist.column.source' }),
            dataIndex: 'source',
            key: 'source',
            render: (value: string) =>
                value === 'charter' ? (
                    <Tag color="gold">
                        {intl.formatMessage({ id: 'admin.waitlist.source.charter' })}
                    </Tag>
                ) : (
                    <Tag>{intl.formatMessage({ id: 'admin.waitlist.source.signup' })}</Tag>
                ),
        },
        {
            title: intl.formatMessage({ id: 'admin.waitlist.column.reason' }),
            key: 'reason',
            render: (_: unknown, record: any) => {
                const reason = waitlistReason(qualificationConfig, record);
                if (!reason) return <span style={{ color: '#bfbfbf' }}>—</span>;
                return (
                    <Tag color={reason.color}>
                        {intl.formatMessage({ id: reason.messageId }, reason.values)}
                    </Tag>
                );
            },
        },
        {
            title: intl.formatMessage({ id: 'admin.waitlist.column.templateType', defaultMessage: 'Template Type' }),
            dataIndex: 'templateType',
            key: 'templateType',
            render: (value: string | null, record: any) => (
                <Select
                    placeholder={intl.formatMessage({ id: 'admin.waitlist.column.templateTypePlaceholder', defaultMessage: 'Select template type' })}
                    style={{ width: 200 }}
                    value={value || undefined}
                    allowClear
                    onChange={(next) => handleTemplateTypeChange(record.id, next || null)}
                    options={templateTypeOptions}
                    disabled={record.status === 'CONVERTED'}
                />
            ),
        },
        {
            title: intl.formatMessage({ id: 'admin.waitlist.column.status' }),
            dataIndex: 'status',
            key: 'status',
            render: (value: string) => (
                <Tag color={STATUS_COLORS[value] || 'default'}>{value}</Tag>
            ),
        },
        {
            title: intl.formatMessage({ id: 'admin.waitlist.column.date' }),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (value: string) => dayjs(value).format('DD MMM YYYY'),
        },
        {
            title: intl.formatMessage({ id: 'admin.waitlist.column.actions' }),
            key: 'actions',
            render: (_, record) => {
                if (record.status === 'CONVERTED') return null;

                const isReinvite = record.inviteCount > 0;
                const canInvite = !!record.templateType;

                return (
                    <Space direction="vertical" size={4}>
                        <Popconfirm
                            title={intl.formatMessage({ id: isReinvite ? 'admin.waitlist.reinviteConfirm' : 'admin.waitlist.inviteConfirm' })}
                            description={intl.formatMessage({ id: 'admin.waitlist.inviteConfirmDesc' }, { email: record.email })}
                            onConfirm={() => handleInvite(record.id, record.email)}
                            okText={intl.formatMessage({ id: 'admin.waitlist.inviteConfirmYes' })}
                            cancelText={intl.formatMessage({ id: 'admin.waitlist.inviteConfirmNo' })}
                            disabled={!canInvite}
                        >
                            <Button
                                type="primary"
                                size="small"
                                icon={<SendOutlined style={{ marginRight: 4 }} />}
                                loading={inviteResult.isLoading}
                                disabled={!canInvite}
                                title={canInvite ? undefined : intl.formatMessage({ id: 'admin.waitlist.inviteDisabledHint' })}
                            >
                                {intl.formatMessage({ id: isReinvite ? 'admin.waitlist.reinviteButton' : 'admin.waitlist.inviteButton' })}
                            </Button>
                        </Popconfirm>
                        {isReinvite && (
                            <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                {intl.formatMessage(
                                    { id: 'admin.waitlist.inviteStats' },
                                    {
                                        count: record.inviteCount,
                                        date: dayjs(record.lastInvitedAt).format('DD MMM YYYY HH:mm'),
                                    },
                                )}
                            </span>
                        )}
                    </Space>
                );
            },
        },
    ];

    return (
        <div style={{ padding: '24px' }}>
            {contextHolder}
            <Title level={3}>
                <TeamOutlined style={{ marginRight: 8 }} />
                {intl.formatMessage({ id: 'admin.waitlist.title' })}
            </Title>

            {stats && stats.length > 0 && (
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    {stats.map((stat) => (
                        <Col key={stat.status} xs={12} sm={8} md={6}>
                            <Card size="small">
                                <Statistic title={stat.status} value={stat.count} />
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            <Card style={{ marginBottom: 24 }}>
                <Space wrap>
                    <Select
                        placeholder={intl.formatMessage({ id: 'admin.waitlist.filter.allStatuses' })}
                        allowClear
                        style={{ width: 200 }}
                        onChange={(value) => setStatusFilter(value)}
                        value={statusFilter}
                    >
                        <Select.Option value="PENDING">PENDING</Select.Option>
                        <Select.Option value="NOTIFIED">NOTIFIED</Select.Option>
                        <Select.Option value="CONVERTED">CONVERTED</Select.Option>
                    </Select>
                </Space>
            </Card>

            <ResponsiveTable
                columns={columns}
                dataSource={entries || []}
                loading={isLoading}
                rowKey="id"
                pagination={{ pageSize: 20 }}
            />
        </div>
    );
};

export default AdminWaitlistPage;
